import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { makeUserLogin } from "../../../store/AuthSlice";
import "./login.scss";
import React, { useEffect, useState } from "react";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import LoadingShadow from "../../../components/loading-shadow/loadingShadow";

const RULES = {
  login: {
    required: "Informe seu usuario.",
    minLength: { value: 3, message: "Usuario precisa de pelo menos 3 caracteres." },
  },
  password: {
    required: "Informe sua senha.",
    minLength: { value: 6, message: "Senha precisa de pelo menos 6 caracteres." },
  },
};

type FieldKey = "login" | "password";
type Errors = Partial<Record<FieldKey, string>>;
type Touched = Partial<Record<FieldKey, boolean>>;

const validate = (login: string, password: string): Errors => {
  const errors: Errors = {};

  if (!login.trim())                          errors.login = RULES.login.required;
  else if (login.length < RULES.login.minLength.value)
                                              errors.login = RULES.login.minLength.message;

  if (!password)                              errors.password = RULES.password.required;
  else if (password.length < RULES.password.minLength.value)
                                              errors.password = RULES.password.minLength.message;

  return errors;
};

const Login: React.FC = () => {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const getAuthLoading = useAppSelector((state) => state.auth.authLoading);

  const [login, setLogin]       = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPass, setShowPass] = useState<boolean>(false);

  const [touched, setTouched]               = useState<Touched>({});
  const [submitAttempted, setSubmitAttempt] = useState<boolean>(false);

  const [apiError, setApiError] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => { setLoading(getAuthLoading); }, [getAuthLoading]);

  const errors = validate(login, password);

  const shouldShowError = (field: FieldKey): boolean =>
    !!errors[field] && (!!touched[field] || submitAttempted);

  const handleBlur = (field: FieldKey) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const onMakeLogiNRequest = async () => {
    setSubmitAttempt(true);
    if (Object.keys(errors).length > 0) return; // bloqueia request se invalido
    setApiError(null);
    setLoading(true);
    const success = await dispatch(makeUserLogin(login, password));
    if (!success) {
      // credenciais invalidas (401) — encerra o loader e mostra a mensagem no form
      setApiError("usuario e/ou senha invalida");
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (loading) return; // evita disparo duplo enquanto a request esta em andamento
      onMakeLogiNRequest();
    }
  };

  const goToRegister = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate("/register");
  };

  const toggle = () => setShowPass(!showPass);

  return (
    <div className="login-wrapper">

      <div className="login-card">
        <h1>
          <img src="https://otc.trkbit.co/assets/imgs/logo-white.png" alt="" />
        </h1>
        <div className="glow"></div>

        <div className="login">

          <div className={`form-group ${shouldShowError("login") ? "has-error" : ""}`}>
            <label htmlFor="user">Usuario:</label>
            <input
              type="text"
              id="user"
              placeholder="login do usuario"
              value={login}
              onChange={(e) => { setLogin(e.target.value); setApiError(null); }}
              onBlur={() => handleBlur("login")}
              onKeyDown={handleKeyDown}
              aria-invalid={shouldShowError("login")}
              aria-describedby={shouldShowError("login") ? "user-error" : undefined}
            />
            {shouldShowError("login") && (
              <small id="user-error" className="form-error">{errors.login}</small>
            )}
          </div>

          <div className={`form-group ${shouldShowError("password") ? "has-error" : ""}`}>
            <label htmlFor="password">Senha:</label>
            <div className="password">
              <input
                type={showPass ? "text" : "password"}
                id="password"
                placeholder=""
                value={password}
                onChange={(e) => { setPassword(e.target.value); setApiError(null); }}
                onBlur={() => handleBlur("password")}
                onKeyDown={handleKeyDown}
                aria-invalid={shouldShowError("password")}
                aria-describedby={shouldShowError("password") ? "password-error" : undefined}
              />
              {showPass
                ? <EyeOff size={20} className="icon" onClick={toggle} />
                : <Eye    size={20} className="icon" onClick={toggle} />}
            </div>
            {shouldShowError("password") && (
              <small id="password-error" className="form-error">{errors.password}</small>
            )}
          </div>

          {/* ERRO DA API (ex.: 401 credenciais invalidas) */}
          {apiError && (
            <div className="form-error-banner" role="alert">{apiError}</div>
          )}

          <div className="buttons start">
            <div className="btn purple font-bold" onClick={onMakeLogiNRequest}>logar-se</div>
            <a href="" className="ghost" onClick={goToRegister}>
              registre-se <ArrowRight size={18} />
            </a>
          </div>

          {loading && <LoadingShadow />}

        </div>
      </div>

    </div>
  );
};

export default Login;
